import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, CalendarClock, Video } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const inputCls = "w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none";
const labelCls = "block text-slate-400 text-xs font-mono uppercase tracking-wider mb-2";

export default function AdminCalls() {
  const [calls, setCalls] = useState([]);
  const [replays, setReplays] = useState([]);
  const [callForm, setCallForm] = useState({ title: "", starts_at: "", join_url: "", note: "" });
  const [replayForm, setReplayForm] = useState({ title: "", recorded_at: "", video_url: "", summary: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hub/content");
      setCalls(data.coaching_calls || []);
      setReplays(data.replays || []);
    } catch {
      toast.error("Failed to load calls");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addCall = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/calls", { ...callForm, starts_at: new Date(callForm.starts_at).toISOString() });
      toast.success("Call scheduled");
      setCallForm({ title: "", starts_at: "", join_url: "", note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add call");
    }
  };

  const addReplay = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/replays", { ...replayForm, recorded_at: new Date(replayForm.recorded_at).toISOString() });
      toast.success("Replay published");
      setReplayForm({ title: "", recorded_at: "", video_url: "", summary: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add replay");
    }
  };

  const remove = async (kind, id) => {
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });

  return (
    <div data-testid="admin-calls" className="grid lg:grid-cols-2 gap-6">
      <div className="bg-charcoal border border-white/10 p-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-5">[SCHEDULE A CALL]</p>
        <form onSubmit={addCall} className="space-y-4" data-testid="call-form">
          <div><label className={labelCls}>Title</label>
            <input required minLength={3} data-testid="call-title-input" value={callForm.title}
              onChange={(e) => setCallForm({ ...callForm, title: e.target.value })} className={inputCls}
              placeholder="Cohort Working Session — Week 3" /></div>
          <div><label className={labelCls}>Date & time</label>
            <input required type="datetime-local" data-testid="call-date-input" value={callForm.starts_at}
              onChange={(e) => setCallForm({ ...callForm, starts_at: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Join link (https, optional)</label>
            <input type="url" data-testid="call-link-input" value={callForm.join_url}
              onChange={(e) => setCallForm({ ...callForm, join_url: e.target.value })} className={inputCls}
              placeholder="https://zoom.us/j/…" /></div>
          <div><label className={labelCls}>Note (optional)</label>
            <input data-testid="call-note-input" value={callForm.note}
              onChange={(e) => setCallForm({ ...callForm, note: e.target.value })} className={inputCls}
              placeholder="Bring your pricing matrix" /></div>
          <button type="submit" data-testid="call-add-btn"
            className="bg-safety hover:bg-safety-hover text-white font-semibold px-6 py-3 text-sm flex items-center gap-2 transition-colors">
            <Plus size={15} /> Schedule call
          </button>
        </form>
        <div className="mt-6 space-y-px border border-white/10">
          {calls.map((c) => (
            <div key={c.call_id} className="bg-navy px-4 py-3.5 flex items-center gap-3" data-testid={`admin-call-row-${c.call_id.slice(0, 8)}`}>
              <CalendarClock size={15} className="text-safety shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{c.title}</p>
                <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">{fmt(c.starts_at)}{c.join_url ? " · link set" : " · no link"}</p>
              </div>
              <button onClick={() => remove("calls", c.call_id)} data-testid={`call-delete-${c.call_id.slice(0, 8)}`}
                className="text-slate-600 hover:text-red-400 transition-colors p-1"><Trash2 size={15} /></button>
            </div>
          ))}
          {calls.length === 0 && <p className="bg-navy p-4 text-slate-500 text-sm">No calls scheduled.</p>}
        </div>
      </div>

      <div className="bg-charcoal border border-white/10 p-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-5">[PUBLISH A REPLAY]</p>
        <form onSubmit={addReplay} className="space-y-4" data-testid="replay-form">
          <div><label className={labelCls}>Title</label>
            <input required minLength={3} data-testid="replay-title-input" value={replayForm.title}
              onChange={(e) => setReplayForm({ ...replayForm, title: e.target.value })} className={inputCls}
              placeholder="Week 2 — Estimates working session" /></div>
          <div><label className={labelCls}>Recorded on</label>
            <input required type="date" data-testid="replay-date-input" value={replayForm.recorded_at}
              onChange={(e) => setReplayForm({ ...replayForm, recorded_at: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Video URL (YouTube / Vimeo / https)</label>
            <input required type="url" data-testid="replay-url-input" value={replayForm.video_url}
              onChange={(e) => setReplayForm({ ...replayForm, video_url: e.target.value })} className={inputCls}
              placeholder="https://youtube.com/watch?v=…" /></div>
          <div><label className={labelCls}>Summary (optional)</label>
            <textarea rows={2} data-testid="replay-summary-input" value={replayForm.summary}
              onChange={(e) => setReplayForm({ ...replayForm, summary: e.target.value })}
              className={`${inputCls} resize-none`} placeholder="What was covered…" /></div>
          <button type="submit" data-testid="replay-add-btn"
            className="bg-safety hover:bg-safety-hover text-white font-semibold px-6 py-3 text-sm flex items-center gap-2 transition-colors">
            <Plus size={15} /> Publish replay
          </button>
        </form>
        <div className="mt-6 space-y-px border border-white/10">
          {replays.map((r) => (
            <div key={r.replay_id} className="bg-navy px-4 py-3.5 flex items-center gap-3" data-testid={`admin-replay-row-${r.replay_id.slice(0, 8)}`}>
              <Video size={15} className="text-safety shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{r.title}</p>
                <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">{new Date(r.recorded_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => remove("replays", r.replay_id)} data-testid={`replay-delete-${r.replay_id.slice(0, 8)}`}
                className="text-slate-600 hover:text-red-400 transition-colors p-1"><Trash2 size={15} /></button>
            </div>
          ))}
          {replays.length === 0 && <p className="bg-navy p-4 text-slate-500 text-sm">No replays published yet.</p>}
        </div>
      </div>
    </div>
  );
}
